import type { ComponentPropsWithoutRef, ElementType, ReactNode } from 'react';

import { cn, cva, type VariantProps } from '@lib/utils';

const flexVariants = cva(['flex'], {
  variants: {
    direction: {
      row: 'flex-row',
      column: 'flex-col',
      'row-reverse': 'flex-row-reverse',
      'column-reverse': 'flex-col-reverse',
    },
    align: {
      start: 'items-start',
      center: 'items-center',
      end: 'items-end',
      stretch: 'items-stretch',
      baseline: 'items-baseline',
    },
    justify: {
      start: 'justify-start',
      center: 'justify-center',
      end: 'justify-end',
      between: 'justify-between',
      around: 'justify-around',
      evenly: 'justify-evenly',
    },
    wrap: {
      nowrap: 'flex-nowrap',
      wrap: 'flex-wrap',
      'wrap-reverse': 'flex-wrap-reverse',
    },
    gap: {
      '0': 'gap-0',
      '0.5': 'gap-0.5',
      '1': 'gap-1',
      '1.5': 'gap-1.5',
      '2': 'gap-2',
      '2.5': 'gap-2.5',
      '3': 'gap-3',
      '3.5': 'gap-3.5',
      '4': 'gap-4',
      '5': 'gap-5',
      '6': 'gap-6',
      '7': 'gap-7',
      '8': 'gap-8',
      '9': 'gap-9',
      '10': 'gap-10',
      '11': 'gap-11',
      '12': 'gap-12',
    },
    inline: {
      true: 'inline-flex',
      false: 'flex',
    },
  },
  defaultVariants: {
    inline: false,
  },
});

export type FlexProps<T extends ElementType = 'div'> = {
  /** The element type to render as */
  as?: T;
  /** Child elements */
  children?: ReactNode;
} & VariantProps<typeof flexVariants> &
  Omit<ComponentPropsWithoutRef<T>, 'children'>;

function Flex<T extends ElementType = 'div'>({
  as,
  className,
  children,
  direction,
  align,
  justify,
  wrap,
  gap,
  inline,
  ...rest
}: FlexProps<T>) {
  const Component = as ?? 'div';

  return (
    <Component
      className={cn(flexVariants({ direction, align, justify, wrap, gap, inline }), className)}
      {...rest}
    >
      {children}
    </Component>
  );
}

Flex.displayName = 'Flex';

export { Flex, flexVariants };
